/**
 * @fileoverview Input/output schemas for the plot-synopsis-merge-v3 agent.
 *
 * This agent merges multiple sequential chunk synopses into a single
 * coherent synopsis. It is the "reduce" step in the map-reduce synopsis pipeline.
 *
 * Input is an array of pre-generated chunk synopses (NOT raw source text).
 * Output is a single merged synopsis.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = BaseFlowInputSchema.extend({
  /** Array of chunk synopses to merge, in sequential order. */
  chunkSynopses: z.array(z.string()).min(2).describe(
    'Array of chunk synopses to merge into a single coherent synopsis. Must be in sequential story order.'
  ),

  /** Target number of tokens for the final merged synopsis. */
  targetTokens: z.number().describe('Target number of tokens for the final merged synopsis.'),

  /** Title of the book/story, if known. */
  title: z.string().optional().describe('Title of the book/story being summarized.'),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

export const OutputSchema = z.object({
  /** The merged, coherent synopsis combining all chunk synopses. */
  synopsis: z.string().describe('A single coherent synopsis merging all chunk synopses, with redundancy removed and transitions smoothed.'),
});

export type Output = z.infer<typeof OutputSchema>;
